import * as tslib_1 from "tslib";
import { EventEmitter } from '@angular/core';
import Dexie from 'dexie';
import * as moment from 'moment';
var DAY_FORMAT = 'YYYY/MM/DD';
export var ENERGY_ID = 208;
export var CARBS_ID = 205;
export var SUGAR_ID = 269;
export var FIBER_ID = 291;
export var PROTEIN_ID = 203;
export var FAT_ID = 204;
export var JOULES_ID = 268;
export var ActivityLevel;
(function (ActivityLevel) {
    ActivityLevel["Active"] = "Active";
    ActivityLevel["SomewhatActive"] = "Somewhat Active";
    ActivityLevel["Sedentary"] = "Sedentary";
})(ActivityLevel || (ActivityLevel = {}));
export var MealName;
(function (MealName) {
    MealName["Breakfast"] = "Breakfast";
    MealName["Lunch"] = "Lunch";
    MealName["Dinner"] = "Dinner";
    MealName["Tea"] = "Tea";
    MealName["Snack"] = "Snack";
})(MealName || (MealName = {}));
var Day = /** @class */ (function () {
    function Day(date, meals, id) {
        this.meals = meals;
        this.id = id;
        if (typeof date === 'number') {
            this.date = moment(date);
        }
        else {
            this.date = date;
        }
    }
    Day.prototype.calories = function () {
        return this.meals.reduce(function (acc, meal) { return acc + meal.calories(); }, 0);
    };
    Day.prototype.carbs = function () {
        return this.meals.reduce(function (a, m) { return a + m.carbs(); }, 0);
    };
    Day.prototype.fat = function () {
        return this.meals.reduce(function (a, m) { return a + m.fat(); }, 0);
    };
    Day.prototype.protein = function () {
        return this.meals.reduce(function (a, m) { return a + m.protein(); }, 0);
    };
    Day.prototype.sortedMeals = function () {
        return this.meals.sort(function (lhs, rhs) {
            return lhs.sortValue() - rhs.sortValue();
        });
    };
    Day.prototype.toJson = function () {
        var ret = {
            date: +this.date,
            meals: this.meals,
        };
        if (this.id) {
            ret.id = this.id;
        }
        return ret;
    };
    return Day;
}());
export { Day };
var Meal = /** @class */ (function () {
    function Meal(name, time, contents, dayId, id) {
        this.name = name;
        this.time = time;
        this.contents = contents;
        this.dayId = dayId;
        this.id = id;
    }
    Meal.prototype.calories = function () {
        return this.contents.reduce(function (a, c) { return a + c.calories; }, 0);
    };
    Meal.prototype.carbs = function () {
        return this.contents.reduce(function (a, c) { return a + c.carbs || 0; }, 0);
    };
    Meal.prototype.fat = function () {
        return this.contents.reduce(function (a, c) { return a + c.fat || 0; }, 0);
    };
    Meal.prototype.protein = function () {
        return this.contents.reduce(function (a, c) { return a + c.protein || 0; }, 0);
    };
    Meal.prototype.sortValue = function () {
        var weight = 0;
        switch (this.name) {
            case MealName.Lunch:
                weight = .01;
                break;
            case MealName.Tea:
                weight = .02;
                break;
            case MealName.Dinner:
                weight = .03;
                break;
            case MealName.Snack:
                weight = .04;
                break;
        }
        return ((this.time.hours * 60) + this.time.minutes) + weight;
    };
    Meal.prototype.formattedTime = function () {
        var hours = this.time.hours > 12
            ? this.time.hours - 12
            : this.time.hours;
        var suffix = this.time.hours >= 12
            ? 'p'
            : 'a';
        var minutes = ("0" + this.time.minutes).substr(-2);
        return hours + ":" + minutes + " " + suffix;
    };
    Meal.prototype.formattedName = function (short) {
        switch (this.name) {
            case MealName.Breakfast:
                return short ? 'B' : this.name;
            case MealName.Lunch:
                return short ? 'L' : this.name;
            case MealName.Dinner:
                return short ? 'D' : this.name;
            case MealName.Snack:
                return short ? 'S' : this.name;
            case MealName.Tea:
                return short ? 'T' : this.name;
            default:
                return short ? '?' : 'Unknown';
        }
    };
    return Meal;
}());
export { Meal };
var MealItem = /** @class */ (function () {
    function MealItem(name, calories, carbs, protein, fat, mealId, id) {
        this.name = name;
        this.calories = calories;
        this.carbs = carbs;
        this.protein = protein;
        this.fat = fat;
        this.mealId = mealId;
        if (id) {
            this.id = id;
        }
    }
    return MealItem;
}());
export { MealItem };
export var MetabolismGender;
(function (MetabolismGender) {
    MetabolismGender["Male"] = "Male";
    MetabolismGender["Female"] = "Female";
})(MetabolismGender || (MetabolismGender = {}));
var Database = /** @class */ (function (_super) {
    tslib_1.__extends(Database, _super);
    function Database(vers) {
        var _this = _super.call(this, 'nutrition-data') || this;
        _this.syncableChanges = new EventEmitter();
        _this.renderableChanges = new EventEmitter();
        _this.version(1).stores({
            foods: '++id,desc,manufacturer',
            weights: 'id,foodDescId,measurementDesc',
            seeds: '++id,when,state',
            users: '$$id,updated',
            days: '$$id,date',
            meals: '$$id,dayId,name,time',
            mealItems: '$$id,name,mealId',
            dropboxInfo: '$$id',
            dropboxHash: '$$id,timestamp',
        });
        _this.version(2).stores({
            foods: '++id,desc,manufacturer',
            weights: 'id,foodDescId,measurementDesc',
            seeds: '++id,when,state',
            users: '$$id,updated',
            days: '$$id,date',
            meals: '$$id,dayId,name,time',
            mealItems: '$$id,name,mealId',
            dropboxInfo: '$$id',
            dropboxHash: '$$id,timestamp',
            userPrefs: '$$id',
        });
        _this.version(3).stores({
            foods: '++id,desc,manufacturer',
            weights: 'id,foodDescId,measurementDesc',
            seeds: '++id,when,state',
            users: '$$id,updated',
            days: '$$id,date',
            meals: '$$id,dayId,name,time',
            mealItems: '$$id,name,mealId',
            dropboxInfo: '$$id',
            dropboxHash: '$$id,timestamp',
            userPrefs: '$$id',
            weightSets: '$$id,name,weight,reps,when',
        });
        return _this;
    }
    /**
     * Check if this database instance has been seeded
     */
    Database.prototype.hasBeenSeeded = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.seeds.count()];
                    case 1: return [2 /*return*/, (_a.sent()) >= 1];
                }
            });
        });
    };
    /**
     * Check if the database is ready to be searched
     */
    Database.prototype.isReady = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var lastUpdate;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.seeds.orderBy('id').last()];
                    case 1:
                        lastUpdate = _a.sent();
                        return [2 /*return*/, lastUpdate.state === 'complete'];
                }
            });
        });
    };
    /**
     * Add a new user to the database
     * @param user The user information to add
     */
    Database.prototype.addUser = function (info) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (info.id) {
                            delete info.id;
                        }
                        if (!info.updated) {
                            info.updated = +moment(); // unix ms timestamp
                        }
                        if (typeof info.updated !== 'number') {
                            info.updated = +info.updated; // unix ms timestamp
                        }
                        return [4 /*yield*/, this.users.add(info)];
                    case 1:
                        _a.sent();
                        this.renderableChanges.emit();
                        return [2 /*return*/];
                }
            });
        });
    };
    Database.prototype.updateUser = function (user) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var existing;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.users.get(user.id)];
                    case 1:
                        existing = _a.sent();
                        existing.deleted = true;
                        delete user.id;
                        if (!user.updated) {
                            user.updated = +moment(); // unix ms timestamp
                        }
                        if (typeof user.updated !== 'number') {
                            user.updated = +user.updated; // unix ms timestamp
                        }
                        return [4 /*yield*/, this.users.put(existing)];
                    case 2:
                        _a.sent();
                        return [4 /*yield*/, this.users.add(user)];
                    case 3:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    Database.prototype.getUserPrefs = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _this = this;
            return tslib_1.__generator(this, function (_a) {
                console.log('getting user preferences');
                return [2 /*return*/, this.userPrefs
                        .limit(1)
                        .first()
                        .then(function (prefs) { return prefs || _this.defaultPrefs(); })];
            });
        });
    };
    Database.prototype.setGenderPref = function (newValue) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var current;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getUserPrefs()];
                    case 1:
                        current = _a.sent();
                        current.metabolismGender = newValue;
                        return [4 /*yield*/, this.userPrefs.put(current)];
                    case 2:
                        _a.sent();
                        return [2 /*return*/, newValue];
                }
            });
        });
    };
    Database.prototype.defaultPrefs = function () {
        return {
            metabolismGender: MetabolismGender.Female
        };
    };
    /**
     * Get the last entry for the user's history
     */
    Database.prototype.getLatestUser = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this
                            .users
                            .orderBy('updated')
                            .reverse()
                            .and(function (u) { return !u.deleted; })
                            .first()];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    /**
     * Get the last 50 user history entries
     */
    Database.prototype.getUserHistory = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var fromDb;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.users
                            .orderBy('updated')
                            .reverse()
                            .limit(50)
                            .and(function (b) { return !b.deleted; })
                            .toArray()];
                    case 1:
                        fromDb = _a.sent();
                        return [2 /*return*/, fromDb.map(function (u) {
                                if (u.updated) {
                                    u.updated = moment(u.updated);
                                }
                                return u;
                            })];
                }
            });
        });
    };
    /**
     * Remove a single history entry
     * @param id The ID of the entry to be removed
     */
    Database.prototype.removeUserEntry = function (id) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var fromDb;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.users.get(id)];
                    case 1:
                        fromDb = _a.sent();
                        fromDb.deleted = true;
                        return [4 /*yield*/, this.users.put(fromDb)];
                    case 2:
                        _a.sent();
                        this.syncableChanges.emit();
                        return [2 /*return*/];
                }
            });
        });
    };
    Database.prototype.removeMeal = function (id) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var fromDb;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.meals.get(id)];
                    case 1:
                        fromDb = _a.sent();
                        fromDb.deleted = true;
                        return [4 /*yield*/, this.meals.put(fromDb)];
                    case 2:
                        _a.sent();
                        this.syncableChanges.emit();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Get today's consumed information
     */
    Database.prototype.getTodaysEntries = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                return [2 /*return*/, this.getMealsForDay(moment())];
            });
        });
    };
    /**
     * Get the stored meals for the date
     * @param day The date to get from the db
     */
    Database.prototype.getMealsForDay = function (date) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var dt, day, id;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        dt = +date.startOf('day');
                        return [4 /*yield*/, this.days.where('date').equals(dt).first()];
                    case 1:
                        day = _a.sent();
                        if (!!day) return [3 /*break*/, 3];
                        day = {
                            date: dt,
                            deleted: false,
                        };
                        return [4 /*yield*/, this.days.put(day)];
                    case 2:
                        id = _a.sent();
                        return [2 /*return*/, new Day(date, [], id)];
                    case 3: return [2 /*return*/, this.fillDay(day)];
                }
            });
        });
    };
    Database.prototype.fillDay = function (day) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var dbMeals, ret, i, dbMeal, dbContents, contents;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.meals.where('dayId').equals(day.id).and(function (d) { return !d.deleted; }).toArray()];
                    case 1:
                        dbMeals = _a.sent();
                        console.log('dbMeals', dbMeals);
                        ret = new Day(day.date, new Array(dbMeals.length), day.id);
                        i = 0;
                        _a.label = 2;
                    case 2:
                        if (!(i < dbMeals.length)) return [3 /*break*/, 5];
                        dbMeal = dbMeals[i];
                        return [4 /*yield*/, this.mealItems
                                .where('mealId')
                                .equals(dbMeal.id)
                                .toArray()];
                    case 3:
                        dbContents = _a.sent();
                        contents = dbContents.map(function (dbItem) {
                            return new MealItem(dbItem.name, dbItem.calories, dbItem.carbs, dbItem.protein, dbItem.fat, dbItem.mealId, dbItem.id);
                        });
                        ret.meals[i] = new Meal(dbMeal.name, dbMeal.time, contents, day.id, dbMeal.id);
                        _a.label = 4;
                    case 4:
                        i++;
                        return [3 /*break*/, 2];
                    case 5: return [2 /*return*/, ret];
                }
            });
        });
    };
    /**
     * Find a food by name
     * @param term The name to search
     */
    Database.prototype.findFood = function (term) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var foods;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.mealItems
                            .where('name')
                            .startsWithIgnoreCase(term)
                            .distinct()
                            .sortBy('name')];
                    case 1:
                        foods = _a.sent();
                        return [2 /*return*/, foods.map(function (f) { return new MealItem(f.name, f.calories, f.carbs, f.protein, f.fat, null, f.id); })];
                }
            });
        });
    };
    /**
     * Get the details for a food from the search
     * @param foodDesId The ID of the food details to get
     */
    Database.prototype.foodDetails = function (foodDesId) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.foods.get(foodDesId)];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        });
    };
    Database.prototype.seed = function (updateCb) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var e_1;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        _a.trys.push([0, 6, , 7]);
                        return [4 /*yield*/, this.seeds.count()];
                    case 1:
                        if ((_a.sent()) > 0) {
                            this.seeds.clear();
                        }
                        return [4 /*yield*/, this.seeds.add({ when: moment().toISOString(), state: 'start' })];
                    case 2:
                        _a.sent();
                        return [4 /*yield*/, this.seedTable(this.foods, 'food_details.json', updateCb)];
                    case 3:
                        _a.sent();
                        return [4 /*yield*/, this.seedTable(this.weights, 'weight.json', updateCb)];
                    case 4:
                        _a.sent();
                        return [4 /*yield*/, this.seeds.add({ when: moment().toISOString(), state: 'complete' })];
                    case 5:
                        _a.sent();
                        return [3 /*break*/, 7];
                    case 6:
                        e_1 = _a.sent();
                        console.error('Error seeding', e_1);
                        return [3 /*break*/, 7];
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    Database.prototype.seedTable = function (table, route, updateCb) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var seedData, e_2, e_3, e_4;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        updateCb('fetching', table.name, 100, 0);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, fetch(route).then(function (res) { return res.json(); })];
                    case 2:
                        seedData = _a.sent();
                        return [3 /*break*/, 4];
                    case 3:
                        e_2 = _a.sent();
                        console.error('Failed to get seed data for', table.name, 'at route', route, e_2);
                        throw e_2;
                    case 4:
                        updateCb('clearing', table.name, 100, 0);
                        _a.label = 5;
                    case 5:
                        _a.trys.push([5, 7, , 8]);
                        return [4 /*yield*/, table.clear()];
                    case 6:
                        _a.sent();
                        return [3 /*break*/, 8];
                    case 7:
                        e_3 = _a.sent();
                        console.error('Failed to clear ', table.name, e_3);
                        throw e_3;
                    case 8:
                        updateCb('starting-seed', table.name, 100, 0);
                        _a.label = 9;
                    case 9:
                        _a.trys.push([9, 11, , 12]);
                        return [4 /*yield*/, this.batchSeed(table, seedData, updateCb)];
                    case 10:
                        _a.sent();
                        return [3 /*break*/, 12];
                    case 11:
                        e_4 = _a.sent();
                        console.error('failed to add data to ', table.name, seedData);
                        return [3 /*break*/, 12];
                    case 12: return [2 /*return*/];
                }
            });
        });
    };
    Database.prototype.batchSeed = function (table, batch, updateCb) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var start, len, slice, e_5;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        start = 0;
                        updateCb('seeding', table.name, batch.length, start);
                        _a.label = 1;
                    case 1:
                        if (!(start < batch.length - 1)) return [3 /*break*/, 6];
                        len = Math.min(10000, batch.length - start);
                        slice = batch.slice(start, start + len);
                        start += len;
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 4, , 5]);
                        return [4 /*yield*/, table.bulkAdd(slice)];
                    case 3:
                        _a.sent();
                        updateCb('seeding', table.name, batch.length, start);
                        return [3 /*break*/, 5];
                    case 4:
                        e_5 = _a.sent();
                        console.error('bailing after error for', table.name, e_5);
                        return [3 /*break*/, 6];
                    case 5: return [3 /*break*/, 1];
                    case 6:
                        updateCb('complete', table.name, batch.length, batch.length);
                        return [2 /*return*/];
                }
            });
        });
    };
    Database.prototype.getAllUserData = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var dbDays, mealHistory, _i, dbDays_1, day, dbMeals, meals, _a, dbMeals_1, meal, items, bodyHistory, weightSetHistory;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0: return [4 /*yield*/, this.days.toArray()];
                    case 1:
                        dbDays = _b.sent();
                        mealHistory = [];
                        _i = 0, dbDays_1 = dbDays;
                        _b.label = 2;
                    case 2:
                        if (!(_i < dbDays_1.length)) return [3 /*break*/, 9];
                        day = dbDays_1[_i];
                        return [4 /*yield*/, this.meals.where('dayId').equals(day.id).toArray()];
                    case 3:
                        dbMeals = _b.sent();
                        meals = [];
                        _a = 0, dbMeals_1 = dbMeals;
                        _b.label = 4;
                    case 4:
                        if (!(_a < dbMeals_1.length)) return [3 /*break*/, 7];
                        meal = dbMeals_1[_a];
                        return [4 /*yield*/, this.mealItems.where('mealId').equals(meal.id).toArray()];
                    case 5:
                        items = _b.sent();
                        meals.push({
                            id: meal.id,
                            time: meal.time,
                            dayId: day.id,
                            name: meal.name,
                            deleted: meal.deleted,
                            items: items,
                        });
                        _b.label = 6;
                    case 6:
                        _a++;
                        return [3 /*break*/, 4];
                    case 7:
                        mealHistory.push({
                            id: day.id,
                            date: day.date,
                            meals: meals,
                            deleted: day.deleted,
                        });
                        _b.label = 8;
                    case 8:
                        _i++;
                        return [3 /*break*/, 2];
                    case 9: return [4 /*yield*/, this.users.toArray()];
                    case 10:
                        bodyHistory = _b.sent();
                        return [4 /*yield*/, this.weightSets.toArray()];
                    case 11:
                        weightSetHistory = _b.sent();
                        return [2 /*return*/, {
                                mealHistory: mealHistory,
                                bodyHistory: bodyHistory,
                                weightSetHistory: weightSetHistory,
                            }];
                }
            });
        });
    };
    Database.prototype.importArchive = function (archive, syncable) {
        if (syncable === void 0) { syncable = true; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var result, _i, _a, day, existingDay, meals, _b, meals_1, meal, updatedMeal, _c, _d, meal, existingMeal, toInsert, _e, _f, item, existingItem, i, _g, _h, body, existingBody, _j, _k, set, existingSet;
            return tslib_1.__generator(this, function (_l) {
                switch (_l.label) {
                    case 0:
                        console.log('importArchive', archive, syncable);
                        result = {
                            days: 0,
                            meals: 0,
                            items: 0,
                            body: 0,
                            sets: 0,
                        };
                        _i = 0, _a = archive.mealHistory;
                        _l.label = 1;
                    case 1:
                        if (!(_i < _a.length)) return [3 /*break*/, 23];
                        day = _a[_i];
                        return [4 /*yield*/, this.days.where('date').equals(day.date).first()];
                    case 2:
                        existingDay = _l.sent();
                        console.log('day vs existing', day, existingDay);
                        if (!!existingDay) return [3 /*break*/, 4];
                        // day is unknown, add a new day
                        return [4 /*yield*/, this.days.put({
                                id: day.id,
                                date: day.date,
                                deleted: day.deleted
                            })];
                    case 3:
                        // day is unknown, add a new day
                        _l.sent();
                        result.days++;
                        return [3 /*break*/, 12];
                    case 4: return [4 /*yield*/, this.meals.where('dayId').equals(existingDay.id).toArray()];
                    case 5:
                        meals = _l.sent();
                        _b = 0, meals_1 = meals;
                        _l.label = 6;
                    case 6:
                        if (!(_b < meals_1.length)) return [3 /*break*/, 9];
                        meal = meals_1[_b];
                        updatedMeal = Object.assign({}, meal, { dayId: day.id });
                        return [4 /*yield*/, this.meals.put(updatedMeal)];
                    case 7:
                        _l.sent();
                        _l.label = 8;
                    case 8:
                        _b++;
                        return [3 /*break*/, 6];
                    case 9: return [4 /*yield*/, this.days.delete(existingDay.id)];
                    case 10:
                        _l.sent();
                        return [4 /*yield*/, this.days.put(day)];
                    case 11:
                        _l.sent();
                        _l.label = 12;
                    case 12:
                        _c = 0, _d = day.meals;
                        _l.label = 13;
                    case 13:
                        if (!(_c < _d.length)) return [3 /*break*/, 22];
                        meal = _d[_c];
                        return [4 /*yield*/, this.meals.where('id')
                                .equals(meal.id)
                                .first()];
                    case 14:
                        existingMeal = _l.sent();
                        console.log('meal vs existing', meal, existingMeal);
                        if (!(!existingMeal || !this.checkMeals(existingMeal, meal))) return [3 /*break*/, 16];
                        toInsert = {
                            id: meal.id,
                            time: meal.time,
                            dayId: day.id,
                            name: meal.name,
                            deleted: meal.deleted
                        };
                        return [4 /*yield*/, this.meals.put(toInsert)];
                    case 15:
                        _l.sent();
                        result.meals++;
                        return [3 /*break*/, 16];
                    case 16:
                        _e = 0, _f = meal.items;
                        _l.label = 17;
                    case 17:
                        if (!(_e < _f.length)) return [3 /*break*/, 21];
                        item = _f[_e];
                        return [4 /*yield*/, this.mealItems
                                .where('id')
                                .equals(item.id)
                                .first()];
                    case 18:
                        existingItem = _l.sent();
                        console.log('item vs existing', item, existingItem);
                        if (!!existingItem) return [3 /*break*/, 20];
                        i = Object.assign({}, item);
                        i.mealId = meal.id;
                        return [4 /*yield*/, this.mealItems.put(i)];
                    case 19:
                        _l.sent();
                        result.items++;
                        _l.label = 20;
                    case 20:
                        _e++;
                        return [3 /*break*/, 17];
                    case 21:
                        _c++;
                        return [3 /*break*/, 13];
                    case 22:
                        _i++;
                        return [3 /*break*/, 1];
                    case 23:
                        _g = 0, _h = archive.bodyHistory;
                        _l.label = 24;
                    case 24:
                        if (!(_g < _h.length)) return [3 /*break*/, 28];
                        body = _h[_g];
                        return [4 /*yield*/, this.users.where('id').equals(body.id).first()];
                    case 25:
                        existingBody = _l.sent();
                        console.log('body vs existing', body, existingBody);
                        if (!(!existingBody || !this.checkBodies(body, existingBody))) return [3 /*break*/, 27];
                        return [4 /*yield*/, this.users.put(body)];
                    case 26:
                        _l.sent();
                        result.body++;
                        _l.label = 27;
                    case 27:
                        _g++;
                        return [3 /*break*/, 24];
                    case 28:
                        _j = 0, _k = archive.weightSetHistory;
                        _l.label = 29;
                    case 29:
                        if (!(_j < _k.length)) return [3 /*break*/, 33];
                        set = _k[_j];
                        return [4 /*yield*/, this.weightSets.where('id').equals(set.id).first()];
                    case 30:
                        existingSet = _l.sent();
                        if (!!existingSet) return [3 /*break*/, 32];
                        return [4 /*yield*/, this.weightSets.put(set)];
                    case 31:
                        _l.sent();
                        result.sets++;
                        _l.label = 32;
                    case 32:
                        _j++;
                        return [3 /*break*/, 29];
                    case 33:
                        if (syncable) {
                            this.syncableChanges.emit();
                        }
                        this.renderableChanges.emit();
                        return [2 /*return*/, result];
                }
            });
        });
    };
    Database.prototype.checkMeals = function (lhs, rhs) {
        return lhs
            && rhs
            && lhs.name === rhs.name
            && lhs.time.hours === rhs.time.hours
            && lhs.time.minutes === rhs.time.minutes
            && lhs.deleted === rhs.deleted;
    };
    Database.prototype.checkBodies = function (lhs, rhs) {
        return lhs
            && rhs
            && lhs.activityLevel === rhs.activityLevel
            && lhs.age === rhs.age
            && lhs.bodyFatPercentage === rhs.bodyFatPercentage
            && lhs.deleted === rhs.deleted
            && lhs.height === rhs.height
            && lhs.weight === rhs.weight
            && lhs.weightTarget === rhs.weightTarget;
    };
    Database.prototype.addDropBoxInfo = function (info) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.dropboxInfo.add(info)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    Database.prototype.getDropBoxInfo = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                return [2 /*return*/, this.dropboxInfo.limit(1).first()];
            });
        });
    };
    Database.prototype.getLastDropboxHash = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var record;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.dropboxHash.orderBy('timestamp').reverse().limit(1).first()];
                    case 1:
                        record = _a.sent();
                        if (!record) {
                            return [2 /*return*/, null];
                        }
                        return [2 /*return*/, record.fileHash];
                }
            });
        });
    };
    Database.prototype.saveDropboxChanges = function (changes) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.dropboxHash.add({
                            fileHash: changes.fileHash,
                            timestamp: +moment(changes.fileModified),
                        })];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    return Database;
}(Dexie));
export { Database };
